import React from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { usePushNotifications } from "../hooks/usePushNotifications";

/**
 * Compact "Mission Alerts" enrolment card. Hidden on browsers that don't support push,
 * and hidden once the user has explicitly opted in.
 */
export default function PushOptIn() {
  const { supported, subscribed, busy, permission, enable, disable } = usePushNotifications();
  if (!supported) return null;
  // Once subscribed, show a compact "on" pill the user can disable from Profile if they want.
  if (subscribed) return null;
  if (permission === "denied") {
    return (
      <div data-testid="push-denied" className="border border-neutral-800 bg-neutral-950 p-3 mb-4 flex items-center gap-3 text-xs text-neutral-400">
        <BellOff className="w-4 h-4 text-neutral-500" />
        Notifications are blocked. Enable them in your browser settings to get the Monday mission drop.
      </div>
    );
  }
  return (
    <div data-testid="push-optin" className="border border-blue-500/40 bg-gradient-to-br from-blue-500/10 to-transparent p-4 mb-4 flex items-center gap-3">
      <Bell className="w-5 h-5 text-blue-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display text-base uppercase leading-tight">Get the Monday Mission Drop</div>
        <div className="text-xs text-neutral-400 mt-0.5">A fresh 7-day community challenge every Monday — first to join earn bragging rights.</div>
      </div>
      <Button
        data-testid="push-optin-btn"
        size="sm"
        onClick={enable}
        disabled={busy}
        className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8 shrink-0"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Turn On"}
      </Button>
    </div>
  );
}

export function PushToggleRow() {
  const { supported, subscribed, busy, permission, enable, disable } = usePushNotifications();
  if (!supported) return (
    <div className="text-xs text-neutral-500">Push notifications aren't supported on this browser.</div>
  );
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        {subscribed ? <Bell className="w-4 h-4 text-blue-500" /> : <BellOff className="w-4 h-4 text-neutral-500" />}
        <div className="min-w-0">
          <div className="text-sm font-medium">Mission Alerts</div>
          <div className="text-xs text-neutral-500 truncate">
            {permission === "denied" ? "Blocked in browser settings" : subscribed ? "On — Monday mission drop active" : "Off"}
          </div>
        </div>
      </div>
      <Button
        data-testid={subscribed ? "push-disable-btn" : "push-enable-btn"}
        size="sm"
        variant={subscribed ? "outline" : "default"}
        disabled={busy || permission === "denied"}
        onClick={subscribed ? disable : enable}
        className={subscribed
          ? "bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-8"
          : "bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8"}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : subscribed ? "Off" : "On"}
      </Button>
    </div>
  );
}
